// Sprint runner-quarantine-truth (D013-EC-126) — THE runner-availability
// predicate. New (verified no equivalent exists — the three ad-hoc
// predicates this replaces lived in the server strip, retired 2026-09-11 by sprint topbar-banners-retire
// (`isUnhealthy`), `RunnersHealthPill.tsx` (two inline filters), and
// `UsageModal.tsx` (raw `status` projection)).
//
// Why one module: during the fleet-quarantine incident (D013-EC-125)
// the banner said "All servers offline", the capacity dropdown said 12×
// "unavailable", and the usage modal said "ONLINE / LAST SEEN now" —
// simultaneously, about the same fleet. Three surfaces, three
// predicates, three different answers, none true. Every surface that
// renders runner availability MUST derive it from `classifyRunner` so
// the surfaces can no longer contradict each other (Horizontal Reuse:
// consolidate, never add a fourth predicate).
//
// Precedence (spec §3 D1): offline > draining > quarantined > available.
//   - offline     → status !== 'online' (WS gone / incompatible build —
//                   the control plane is not talking to this runner).
//   - draining    → drainMode (operator is taking it out of rotation).
//   - quarantined → online + heartbeating but self-marked with an
//                   `unavailableReason` (credential healthcheck, disk,
//                   auth…). Running agents are NOT affected; pinned
//                   spawns still work (D013-EC-127 founder override) —
//                   only Auto-scheduling skips the runner.
//   - available   → none of the above.

import type { RunnerStatus, RunnerUnavailableReason } from './api';

// Structural subset of `RunnerSnapshot` so both snapshot consumers
// (banner, pill) and joined consumers (usage modal + picker, whose
// `RunnerUsageEntry` rows lack `unavailableReason` and must join with
// the runners store — or fall back to a reason-less input when the
// snapshot has not hydrated yet) can call the same function.
export interface RunnerAvailabilityInput {
  status: RunnerStatus;
  drainMode: boolean;
  unavailableReason: RunnerUnavailableReason | null;
}

export type RunnerAvailability =
  | { state: 'available' }
  | { state: 'offline' }
  | { state: 'draining' }
  | { state: 'quarantined'; reason: RunnerUnavailableReason };

export type RunnerAvailabilityState = RunnerAvailability['state'];

export function classifyRunner(
  runner: RunnerAvailabilityInput,
): RunnerAvailability {
  if (runner.status !== 'online') return { state: 'offline' };
  if (runner.drainMode) return { state: 'draining' };
  // `!= null` (loose) also guards `undefined` — the 2026-05-17 Settings
  // crash class was a Record lookup on an enum value the frontend did
  // not know; a wire row missing the field entirely must classify as
  // available, not throw downstream.
  if (runner.unavailableReason != null) {
    return { state: 'quarantined', reason: runner.unavailableReason };
  }
  return { state: 'available' };
}
